import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Crown, Loader2, Zap, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const PRO_FEATURES = [
  'Unlimited classes & schools',
  'AI lesson summaries',
  'AI behaviour strategies',
  'PDF export',
  'Behaviour trends & analytics',
  'Attendance tracking',
  'Student management',
  'Timer, calculator & tools',
  'Seating plans & team generator',
  'Priority support',
];

export const UpgradePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const r = await fetch(`${API}/user/plan`, { credentials: 'include' });
        if (r.ok) setPlanInfo(await r.json());
      } catch (e) { console.error('Failed to fetch plan:', e); }
    };
    fetchPlan();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setCheckingStatus(true);
    let attempts = 0;

    const poll = async () => {
      try {
        const r = await fetch(`${API}/checkout/status/${sessionId}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          if (data.payment_status === 'paid') {
            setPlanInfo({ plan: 'pro', is_pro: true, trial_days_left: 0 });
            toast.success('Welcome to Reliefify Pro!');
            setCheckingStatus(false);
            return;
          } else if (data.status === 'expired') {
            toast.error('Payment session expired');
            setCheckingStatus(false);
            return;
          }
        }
      } catch (e) { console.error('Poll error:', e); }

      attempts++;
      if (attempts < 5) setTimeout(poll, 2000);
      else setCheckingStatus(false);
    };
    poll();
  }, [sessionId]);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/checkout/create-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (r.ok) {
        const data = await r.json();
        window.location.href = data.url;
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start checkout');
      }
    } catch (e) { toast.error('Failed to start checkout'); }
    finally { setLoading(false); }
  };

  const isPro = planInfo?.plan === 'pro' || planInfo?.plan === 'beta' || planInfo?.is_pro;
  const isTrialExpired = planInfo?.plan === 'trial_expired';
  const isTrialActive = planInfo?.plan === 'trial' && planInfo?.trial_days_left > 0;
  const daysLeft = planInfo?.trial_days_left;

  return (
    <Layout breadcrumb={[{ label: 'Upgrade' }]}>
      <div className="max-w-xl mx-auto space-y-6" data-testid="upgrade-page">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-50 mb-4">
            <Crown className="w-7 h-7 text-amber-500" />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">
            {isPro ? "You're on Pro!" : 'Upgrade to Pro'}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {isPro
              ? 'You have full access to all features'
              : 'Continue using Reliefify with all features unlocked'}
          </p>
        </div>

        {checkingStatus && (
          <Card className="bg-teal-50 border border-teal-200 rounded-xl">
            <CardContent className="p-4 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
              <span className="text-sm text-teal-700">Verifying your payment...</span>
            </CardContent>
          </Card>
        )}

        {isTrialExpired && !isPro && (
          <Card className="bg-amber-50 border border-amber-200 rounded-xl" data-testid="trial-expired-card">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Your trial has ended</p>
                <p className="text-xs text-amber-700 mt-0.5">Upgrade to Pro to continue using Reliefify</p>
              </div>
            </CardContent>
          </Card>
        )}

        {isTrialActive && (
          <Card className="bg-blue-50 border border-blue-200 rounded-xl" data-testid="trial-active-card">
            <CardContent className="p-4 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">Trial Active</p>
                <p className="text-xs text-blue-700 mt-0.5">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining</p>
              </div>
            </CardContent>
          </Card>
        )}

        {isPro && (
          <Card className="bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="pro-active-card">
            <CardContent className="p-4 flex items-center justify-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">You're on the Pro plan! All features unlocked.</span>
            </CardContent>
          </Card>
        )}

        <Card className={`border rounded-xl overflow-hidden ${isPro ? 'border-emerald-300 bg-emerald-50/30' : 'border-teal-300 bg-gradient-to-b from-teal-50/50 to-white'}`}>
          <div className="bg-teal-600 py-1.5 text-center">
            <span className="text-xs text-white font-semibold tracking-wide uppercase">
              {isPro ? 'Your Plan' : 'Reliefify Pro'}
            </span>
          </div>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-4xl font-heading font-bold text-slate-900">
                $4.99<span className="text-base font-normal text-slate-500">/month</span>
              </p>
              <p className="text-sm text-slate-500 mt-2">Everything you need in the classroom</p>
            </div>
            
            <ul className="space-y-3 mb-6">
              {PRO_FEATURES.map((feature, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-teal-500 flex-shrink-0" />{feature}
                </li>
              ))}
            </ul>
            
            {isPro ? (
              <div className="text-center py-3">
                <span className="text-sm text-emerald-600 font-medium flex items-center justify-center gap-1">
                  <Check className="w-4 h-4" />Active Subscription
                </span>
              </div>
            ) : (
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 text-base font-medium"
                data-testid="upgrade-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                {isTrialExpired ? 'Subscribe Now — $4.99/mo' : 'Upgrade to Pro — $4.99/mo'}
              </Button>
            )}
          </CardContent>
        </Card>

        {!isPro && (
          <div className="text-center space-y-2">
            <p className="text-xs text-slate-400">Cancel anytime. No questions asked.</p>
            <button
              onClick={() => navigate('/app/dashboard')}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1 mx-auto"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Back to app
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};
